import { useEffect, useState } from 'react';

import createAnyField from '../fields/AnyField';
import useForm from './useForm';
import useFieldset from './useFieldset';

/**
 * Convert from a field definition into an observable instance of an field.
 * @param {object} field field descriptor (ie. not an instance)
 * @param {string} type type of Input we're getting attached to. May affect the field is initialized.
 * @param {boolean} [isVisible=true]
 * @returns {object}
 */
function useAnyField(field, isVisible = true) {
    const form = useForm();
    const fieldset = useFieldset();

    const [ fieldInstance ] = useState(() => form?.getFieldNamed(field.name) ?? createAnyField(field, form));

    // Keep the created field in-sync with the descriptor.
    useEffect(() => {
        fieldInstance.name = field.name;
    }, [ fieldInstance, field.name ]);

    useEffect(() => {
        if (typeof field.value !== 'undefined') {
            fieldInstance.setValue(field.value);
        }
    }, [ fieldInstance, field.value ]);

    // Register the Field within the Form (if available)
    useEffect(() => {
        if (!form) {
            return;
        }

        // Only register us if it's not already in the Form.
        if (!form.getFieldNamed(fieldInstance.name)) {
            form.addField(fieldInstance);

            // When we unmount, remove the field.
            return () => form.removeField(fieldInstance);
        }
    }, [ form, fieldInstance, fieldInstance.name ]);

    useEffect(() => {
        if (!fieldset) {
            return;
        }

        // Only add if it's not already in the Fieldset.
        if (!fieldset.getFieldNamed(fieldInstance.name)) {
            fieldset.addField(fieldInstance);

            return () => fieldset.removeField(fieldInstance);
        }
    }, [ fieldset, fieldInstance, fieldInstance.name ]);

    // Toggle visibility of field in Form if visible changes
    useEffect(() => {
        if (form && fieldInstance && form.isFieldVisible(fieldInstance.name) !== isVisible) {
            form.setFieldVisibility(fieldInstance, isVisible);
        }
    }, [ form, fieldInstance, isVisible ]);

    return fieldInstance;
}

export default useAnyField;
